import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Search, Eye, CheckCircle, Ban, Activity, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import ClientDetails from './ClientDetails';

const mockClients = [
  { 
    id: 'CLT-001', 
    name: 'ABC Trading LLC', 
    email: 'contact@abctrading.ae', 
    erp: 'SAP', 
    serviceProvider: 'ServicePro UAE',
    status: 'active',
    subscription: 'Premium',
    monthlyInvoices: 1247,
    apiHits: 45632,
    successRate: 98.5,
    lastActive: '2025-10-24 14:32:15'
  },
  { 
    id: 'CLT-002', 
    name: 'XYZ Corporation', 
    email: 'admin@xyzcorp.com', 
    erp: 'Oracle', 
    serviceProvider: 'TaxConnect ME',
    status: 'active',
    subscription: 'Standard',
    monthlyInvoices: 892,
    apiHits: 32456,
    successRate: 97.2,
    lastActive: '2025-10-24 13:15:22'
  },
  { 
    id: 'CLT-003', 
    name: 'Global Enterprises', 
    email: 'info@globalent.ae', 
    erp: 'Tally', 
    serviceProvider: 'ServicePro UAE',
    status: 'suspended',
    subscription: 'Basic',
    monthlyInvoices: 234,
    apiHits: 8765,
    successRate: 89.3,
    lastActive: '2025-10-20 09:45:10'
  },
  { 
    id: 'CLT-004', 
    name: 'Tech Solutions FZ', 
    email: 'admin@techsol.ae', 
    erp: 'Zoho', 
    serviceProvider: 'InvoicePro',
    status: 'active',
    subscription: 'Premium',
    monthlyInvoices: 1856,
    apiHits: 67890,
    successRate: 99.1,
    lastActive: '2025-10-24 14:28:45'
  },
];

interface ClientsManagementProps {
  onViewClient?: (clientId: string) => void;
}

export default function ClientsManagement({ onViewClient }: ClientsManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState(mockClients);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    erp: 'SAP',
    subscription: 'Standard',
    serviceProviderName: '',
    serviceProviderUrl: '',
    salesEndpoint: '/invoices/sales',
    purchaseStatusEndpoint: '/invoices/purchase/status',
    serviceProviderApiKey: ''
  });

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewClient = (clientId: string) => {
    setSelectedClientId(clientId);
    if (onViewClient) {
      onViewClient(clientId);
    }
  };

  const handleBackToList = () => {
    setSelectedClientId(null);
  };

  const handleSuspendClient = (id: string) => {
    setClients(clients.map(c => 
      c.id === id ? { ...c, status: c.status === 'active' ? 'suspended' : 'active' } : c
    ));
    toast.success(clients.find(c => c.id === id)?.status === 'active' ? 'Client suspended successfully' : 'Client activated successfully');
  };

  const handleAddClient = () => {
    if (!newClient.name || !newClient.email || !newClient.serviceProviderName || !newClient.serviceProviderUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    const clientId = `CLT-${String(clients.length + 1).padStart(3, '0')}`;
    const newClientData = {
      id: clientId,
      name: newClient.name,
      email: newClient.email,
      erp: newClient.erp,
      serviceProvider: newClient.serviceProviderName,
      status: 'active' as const,
      subscription: newClient.subscription as 'Basic' | 'Standard' | 'Premium',
      monthlyInvoices: 0,
      apiHits: 0,
      successRate: 0,
      lastActive: new Date().toISOString().slice(0, 19).replace('T', ' ')
    };

    setClients([...clients, newClientData]);
    setIsAddDialogOpen(false);
    toast.success('Client added successfully!');
    
    // Reset form
    setNewClient({
      name: '',
      email: '',
      erp: 'SAP',
      subscription: 'Standard',
      serviceProviderName: '',
      serviceProviderUrl: '',
      salesEndpoint: '/invoices/sales',
      purchaseStatusEndpoint: '/invoices/purchase/status',
      serviceProviderApiKey: ''
    });
  };

  // If a client is selected, show the details page
  if (selectedClientId) {
    return <ClientDetails clientId={selectedClientId} onBack={handleBackToList} />;
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-gray-900">Clients Management</h1>
          <p className="text-gray-600 mt-1">Manage all your SaaS clients and their service provider configurations</p>
        </div>
        <Button 
          className="bg-[#0052CC] hover:bg-[#0041A3]"
          onClick={() => setIsAddDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Client
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clients</p>
                <p className="text-2xl text-gray-900 mt-2">{clients.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl text-gray-900 mt-2">{clients.filter(c => c.status === 'active').length}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Suspended</p>
                <p className="text-2xl text-gray-900 mt-2">{clients.filter(c => c.status === 'suspended').length}</p>
              </div>
              <Ban className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total API Calls</p>
                <p className="text-2xl text-gray-900 mt-2">{(clients.reduce((acc, c) => acc + c.apiHits, 0) / 1000).toFixed(0)}K</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by client name, email, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Clients ({filteredClients.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>ERP System</TableHead>
                <TableHead>Service Provider</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Monthly Invoices</TableHead>
                <TableHead>Success Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow key={client.id} className="cursor-pointer hover:bg-gray-50">
                  <TableCell className="text-[#0052CC]">{client.id}</TableCell>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{client.erp}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{client.serviceProvider}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={client.subscription === 'Premium' ? 'default' : 'secondary'}>
                      {client.subscription}
                    </Badge>
                  </TableCell>
                  <TableCell>{client.monthlyInvoices.toLocaleString()}</TableCell>
                  <TableCell>
                    <span className={`text-sm ${client.successRate > 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {client.successRate}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {client.status === 'active' ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-green-700">Active</span>
                        </>
                      ) : (
                        <>
                          <Ban className="w-4 h-4 text-red-500" />
                          <span className="text-red-700">Suspended</span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewClient(client.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSuspendClient(client.id)}
                      >
                        <Ban className={`w-4 h-4 ${client.status === 'active' ? 'text-red-500' : 'text-green-500'}`} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Configure a new client with their company details and service provider integration settings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input 
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      placeholder="ABC Trading LLC"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      placeholder="contact@company.ae"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>ERP System *</Label>
                    <Select value={newClient.erp} onValueChange={(value) => setNewClient({ ...newClient, erp: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SAP">SAP</SelectItem>
                        <SelectItem value="Oracle">Oracle ERP Cloud</SelectItem>
                        <SelectItem value="Tally">Tally Prime</SelectItem>
                        <SelectItem value="Zoho">Zoho Books</SelectItem>
                        <SelectItem value="Dynamics">Microsoft Dynamics 365</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subscription Plan *</Label>
                    <Select value={newClient.subscription} onValueChange={(value) => setNewClient({ ...newClient, subscription: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Basic">Basic</SelectItem>
                        <SelectItem value="Standard">Standard</SelectItem>
                        <SelectItem value="Premium">Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Service Provider Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700">
                    The service provider is the intermediary that forwards invoices to government authorities (FTA). AIS does not communicate directly with FTA.
                  </p>
                </div>

                <div>
                  <Label>Service Provider Name *</Label>
                  <Input 
                    value={newClient.serviceProviderName}
                    onChange={(e) => setNewClient({ ...newClient, serviceProviderName: e.target.value })}
                    placeholder="ServicePro UAE"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Service Provider API Base URL *</Label>
                  <Input 
                    value={newClient.serviceProviderUrl}
                    onChange={(e) => setNewClient({ ...newClient, serviceProviderUrl: e.target.value })}
                    placeholder="https://api.serviceprovider.com/v2"
                    className="mt-1"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Sales Invoice Endpoint (Outbound) *</Label>
                    <Input 
                      value={newClient.salesEndpoint}
                      onChange={(e) => setNewClient({ ...newClient, salesEndpoint: e.target.value })}
                      placeholder="/invoices/sales"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-600 mt-1">AIS sends sales invoices to this endpoint</p>
                  </div>
                  <div>
                    <Label>Purchase Invoice Status Endpoint (Outbound) *</Label>
                    <Input 
                      value={newClient.purchaseStatusEndpoint}
                      onChange={(e) => setNewClient({ ...newClient, purchaseStatusEndpoint: e.target.value })}
                      placeholder="/invoices/purchase/status"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-600 mt-1">AIS sends accept/reject status to this endpoint</p>
                  </div>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-xs text-gray-700">
                      <strong>Note:</strong> Service Provider will forward purchase invoices to: 
                      <code className="bg-white px-2 py-1 rounded ml-1">
                        https://api.ais-erp.com/v2/[CLIENT_ID]/receive-purchase
                      </code>
                    </p>
                  </div>
                </div>

                <div>
                  <Label>Service Provider API Key *</Label>
                  <Input 
                    type="password"
                    value={newClient.serviceProviderApiKey}
                    onChange={(e) => setNewClient({ ...newClient, serviceProviderApiKey: e.target.value })}
                    placeholder="sp_xxxxx_secure_key"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddClient} className="bg-[#0052CC] hover:bg-[#0041A3]">
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
